// The controller. One hook owns every call to the backend; MobileApp and
// DesktopApp are pure presentation over the value it returns.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, pollGraphJob } from "./api.js";

const nextId = (() => { let n = 0; return (p) => p + "_" + (++n) + "_" + Date.now().toString(36); })();
const stamp = () => {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return p(d.getHours()) + ":" + p(d.getMinutes()) + ":" + p(d.getSeconds());
};

export function useBoardIQ() {
  const [community, setCommunity] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [totalChunks, setTotalChunks] = useState(0);
  const [fatal, setFatal] = useState(null);

  // ── chat (Counsel + Association Laws share this engine) ──────────────
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [debugLines, setDebugLines] = useState([]);
  const abortRef = useRef(null);

  const pushDebug = useCallback((line, kind = "info") => {
    setDebugLines((prev) => [...prev.slice(-60), { id: nextId("dbg"), at: stamp(), line, kind }]);
  }, []);

  const ask = useCallback(async (query, { scope = "all", simplify = false, knowledgeMode = "grounded" } = {}) => {
    const text = String(query || "").trim();
    if (!text || pending) return;

    const history = messages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-8)
      .map((m) => ({ role: m.role, content: m.text }));

    setMessages((prev) => [...prev, { id: nextId("u"), role: "user", text }]);
    setPending(true);
    pushDebug("POST " + api.base + "/search · scope=" + scope + " · mode=" + knowledgeMode, "req");

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const t0 = performance.now();

    try {
      const res = await api.search({ query: text, scope, simplify, knowledgeMode, history }, ctrl.signal);
      const ms = Math.round(performance.now() - t0);
      pushDebug("200 · " + ((res.citations || res.results || []).length) + " passages · " + ms + "ms", "ok");
      setMessages((prev) => [...prev, {
        id: nextId("a"),
        role: "assistant",
        text: res.answer || res.synthesis || res.reply || "",
        sources: (res.citations || res.results || []).map((c, i) => ({
          id: nextId("src"),
          label: c.label || c.section || c.source || ("§" + (i + 1)),
          text: c.text || c.snippet || "",
          document: c.document || c.filename || "",
          score: c.score,
        })),
        unverified: !!res.unverified,
      }]);
    } catch (err) {
      if (err.name === "AbortError") { pushDebug("aborted by user", "err"); return; }
      pushDebug((err.status || "ERR") + " · " + err.message, "err");
      setMessages((prev) => [...prev, { id: nextId("e"), role: "error", text: err.message }]);
    } finally {
      abortRef.current = null;
      setPending(false);
    }
  }, [messages, pending, pushDebug]);

  const stop = useCallback(() => { abortRef.current && abortRef.current.abort(); }, []);
  const clearChat = useCallback(() => { setMessages([]); setDebugLines([]); }, []);

  // ── resolutions ──────────────────────────────────────────────────────
  const [resolutions, setResolutions] = useState([]);
  const [resFilter, setResFilter] = useState("all");
  const [loadingResolutions, setLoadingResolutions] = useState(false);

  const refreshResolutions = useCallback(async (status) => {
    setLoadingResolutions(true);
    try {
      const res = await api.listResolutions(status === "all" ? undefined : status);
      setResolutions(res.resolutions || []);
    } catch (err) {
      pushDebug("GET /resolutions failed · " + err.message, "err");
    } finally {
      setLoadingResolutions(false);
    }
  }, [pushDebug]);

  const approve = useCallback(async (id, signerName) => {
    const updated = await api.approveResolution(id, signerName);
    setResolutions((prev) => prev.map((r) => (r.id === id ? updated : r)));
    window.dispatchEvent(new CustomEvent("hoa:resolution-approved", { detail: id }));
    return updated;
  }, []);

  const saveDraft = useCallback(async ({ title, text, createdBy }) => {
    const created = await api.createResolution({ title, text, createdBy });
    setResolutions((prev) => [created, ...prev]);
    return created;
  }, []);

  const editDraft = useCallback(async (id, patch) => {
    const updated = await api.patchResolution(id, patch);
    setResolutions((prev) => prev.map((r) => (r.id === id ? updated : r)));
    return updated;
  }, []);

  const removeDraft = useCallback(async (id) => {
    await api.deleteResolution(id);
    setResolutions((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // ── drafting conversation ────────────────────────────────────────────
  const [draftTurns, setDraftTurns] = useState([]);
  const [draft, setDraft] = useState({ text: "", title: "", ready: false });
  const [draftPending, setDraftPending] = useState(false);

  const sendDraftMessage = useCallback(async (message) => {
    const text = String(message || "").trim();
    if (!text || draftPending) return;

    const history = draftTurns.map((t) => ({ role: t.role, content: t.text }));
    setDraftTurns((prev) => [...prev, { id: nextId("du"), role: "user", text }]);
    setDraftPending(true);

    try {
      const res = await api.resolutionChat({ message: text, history });
      setDraftTurns((prev) => [...prev, {
        id: nextId("da"),
        role: "assistant",
        text: res.reply || "",
        options: res.options || null,
      }]);
      if (res.draft || res.title || typeof res.ready === "boolean") {
        setDraft((prev) => ({
          text: res.draft ?? prev.text,
          title: res.title ?? prev.title,
          ready: !!res.ready,
        }));
      }
    } catch (err) {
      setDraftTurns((prev) => [...prev, { id: nextId("de"), role: "error", text: err.message }]);
    } finally {
      setDraftPending(false);
    }
  }, [draftTurns, draftPending]);

  const resetDraft = useCallback(() => {
    setDraftTurns([]);
    setDraft({ text: "", title: "", ready: false });
  }, []);

  // Approving finishes the drafting effort app-wide.
  useEffect(() => {
    const onApproved = () => resetDraft();
    window.addEventListener("hoa:resolution-approved", onApproved);
    return () => window.removeEventListener("hoa:resolution-approved", onApproved);
  }, [resetDraft]);

  // ── onboarding / uploads, with the upload console ────────────────────
  const [uploadLog, setUploadLog] = useState([]);
  const [uploads, setUploads] = useState([]); // {id, filename, label, status, pct, sections, error}

  const logUpload = useCallback((line, kind = "info") => {
    setUploadLog((prev) => [...prev.slice(-80), { id: nextId("ul"), at: stamp(), line, kind }]);
  }, []);

  const patchUpload = useCallback((id, patch) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }, []);

  const saveCommunityName = useCallback(async (name) => {
    logUpload("POST " + api.base + "/community/name · " + name, "req");
    const res = await api.setCommunityName(name);
    setCommunity((prev) => ({ ...(prev || {}), name: res.name || name }));
    logUpload("200 · community name saved", "ok");
    return res;
  }, [logUpload]);

  /** CC&Rs upload → index → poll the knowledge-graph job. */
  const uploadCcrs = useCallback(async (file) => {
    const id = nextId("up");
    setUploads((prev) => [...prev, { id, filename: file.name, label: "CC&Rs", status: "uploading", pct: 8 }]);
    logUpload("POST " + api.base + "/community/ccrs · " + file.name, "req");

    const tick = setInterval(() => {
      setUploads((prev) => prev.map((u) => (u.id === id && u.pct < 92 ? { ...u, pct: u.pct + 9 } : u)));
    }, 240);

    try {
      const res = await api.uploadCcrs(file);
      clearInterval(tick);
      const sections = res.ccr_chunks ?? res.total_chunks ?? 0;
      patchUpload(id, { pct: 100, status: "indexing", sections });
      setTotalChunks(res.total_chunks || 0);
      logUpload("200 · " + sections + " CC&R sections · " + (res.statute_chunks || 0) + " statute chunks", "ok");

      if (res.graph_job_id) {
        logUpload("GET " + api.base + "/graph/jobs/" + res.graph_job_id + " · polling", "req");
        const job = await pollGraphJob(res.graph_job_id, (j) => {
          const pct = j.total ? Math.round((j.current / j.total) * 100) : null;
          logUpload((j.stage || j.status) + (pct !== null ? " · " + pct + "%" : "") + (j.message ? " · " + j.message : ""), "info");
        });
        if (job.status === "failed") {
          patchUpload(id, { status: "failed", error: job.error || "graph ingestion failed" });
          logUpload("graph job failed · " + (job.error || "unknown"), "err");
          return res;
        }
      }

      patchUpload(id, { status: "indexed" });
      logUpload("indexed · " + file.name + " searchable", "ok");
      return res;
    } catch (err) {
      clearInterval(tick);
      patchUpload(id, { status: "failed", error: err.message });
      logUpload((err.status || "ERR") + " · " + err.message, "err");
      throw err;
    }
  }, [logUpload, patchUpload]);

  const uploadDocument = useCallback(async (file, title) => {
    const id = nextId("up");
    setUploads((prev) => [...prev, { id, filename: file.name, label: title || "Document", status: "uploading", pct: 10 }]);
    logUpload("POST " + api.base + "/community/documents · " + file.name, "req");
    try {
      const res = await api.uploadDocument(file, title);
      patchUpload(id, { pct: 100, status: "indexed", sections: res.other_chunks });
      setDocuments(res.documents || []);
      setTotalChunks(res.total_chunks || 0);
      logUpload("200 · " + (res.other_chunks || 0) + " sections indexed", "ok");
      return res;
    } catch (err) {
      patchUpload(id, { status: "failed", error: err.message });
      logUpload((err.status || "ERR") + " · " + err.message, "err");
      throw err;
    }
  }, [logUpload, patchUpload]);

  const removeDocument = useCallback(async (id) => {
    logUpload("DELETE " + api.base + "/community/documents/" + id, "req");
    await api.deleteDocument(id);
    setDocuments((prev) => prev.filter((d) => d.id !== id));
    logUpload("200 · document removed", "ok");
  }, [logUpload]);

  // ── bootstrap ────────────────────────────────────────────────────────
  const loadCommunity = useCallback(async () => {
    try {
      const info = await api.getCommunity();
      setCommunity(info);
      setDocuments(info.other_docs || []);
    } catch (err) {
      if (err.status && err.status !== 404) setFatal(err.message);
    }
  }, []);

  useEffect(() => { loadCommunity(); refreshResolutions("all"); }, [loadCommunity, refreshResolutions]);

  const visibleResolutions = useMemo(
    () => (resFilter === "all" ? resolutions : resolutions.filter((r) => r.status === resFilter)),
    [resolutions, resFilter]
  );

  return {
    // community
    community, documents, totalChunks, fatal, loadCommunity,
    saveCommunityName, uploadCcrs, uploadDocument, removeDocument,
    uploads, uploadLog,
    // chat
    messages, pending, ask, stop, clearChat, debugLines,
    // resolutions
    resolutions, visibleResolutions, resFilter, setResFilter, loadingResolutions,
    refreshResolutions, approve, saveDraft, editDraft, removeDraft,
    // drafting
    draftTurns, draft, setDraft, draftPending, sendDraftMessage, resetDraft,
  };
}
